import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { User } from "../../models/User";
import { AuthService } from "../../services/authService";
import { UserService } from "../../services/userService";
import { UpdateValues } from "../../validation/RegisterValidation";
import { monetaryZonesServices } from "../../services/monetaryZonesServices";
import { CreateCountryDto } from "../../models/MonetaryZones";

export interface AuthState {
  currentUser: User | null;
  errors: string | null;
  loading: boolean;
  otpVerified: boolean;
  customerMode: string;
  countries: CreateCountryDto[];
}

const initialState: AuthState = {
  currentUser: null,
  errors: null,
  loading: false,
  otpVerified: false,
  customerMode: 'SANDBOX',
  countries:[],
};

export const logout = createAsyncThunk<void>(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await AuthService.logout();
      dispatch(resetAuthState());
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

export const sendOtp = createAsyncThunk<void, string>(
  "auth/sendOtp",
  async (email, { rejectWithValue }) => {
    try {
      await AuthService.sendOtp(email);
    } catch (error) {
      return rejectWithValue("Failed to send OTP");
    }
  }
);

export const verifyOtp = createAsyncThunk<void, { email: string; otp: string }>(
  "auth/verifyOtp",
  async (data, { rejectWithValue }) => {
    try {
      await AuthService.verifyOtp(data);
    } catch (error) {
      return rejectWithValue("Invalid OTP");
    }
  }
);

export const getCurrentUser = createAsyncThunk<User>(
  "auth/current",
  async (_, { rejectWithValue }) => {
    try {
      const res = await AuthService.getCurrentUser();
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get user"
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk<User, Partial<UpdateValues>>(
  "auth/updateUserProfile",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const updatedUser = await UserService.updateUser(userData);
      dispatch(getCurrentUser());
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const getAllCountry = createAsyncThunk<CreateCountryDto[]>(
  "countries/all",
  async (_, thunkAPI) => {
    try {
      const res = await monetaryZonesServices.getAllCountry();
      return res.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch transactions";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.currentUser = null;
      state.errors = null;
      state.loading = false;
      state.otpVerified = false;
      state.customerMode = 'SANDBOX';
      // Nettoyer la session OTP à chaque déconnexion
      // pour forcer la ré-vérification OTP à la prochaine connexion
      localStorage.removeItem("otpConfirmed");
    },
    setCustomerMode: (state, action: { payload: string }) => {
      state.customerMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.errors = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.currentUser = { ...state.currentUser, ...action.payload };
        state.loading = false;
        state.errors = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message || "Failed to send OTP";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message || "Invalid OTP";
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        if (action.payload.customer) {
          state.customerMode = action.payload.customer.mode;
        }
        state.loading = false;
        state.errors = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message || "Failed to fetch user";
      })
      .addCase(getAllCountry.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllCountry.fulfilled, (state, action) => {
        state.countries = action.payload;
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllCountry.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      });
  },
});

export const { resetAuthState, setCustomerMode } = authSlice.actions;

export default authSlice.reducer;
